import React, { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import { getAdminToken } from '../modules/admin/auth/_requests'

// ADMIN-ONLY: view / generate / edit a post's variations (alternative wordings
// the bot publishes at random when the account has post variations on).
// Backed by /admin/post-variations (admin role enforced server-side).

const API_URL = process.env.REACT_APP_API_URL
const adminHeaders = (): Record<string, string> => {
  const t = getAdminToken()
  const h: Record<string, string> = {}
  if (t) h.Authorization = `Bearer ${t}`
  return h
}
const api = {
  get: (url: string) => axios.get(`${API_URL}/admin/post-variations${url}`, { headers: adminHeaders(), withCredentials: true }),
  post: (url: string, body: any) => axios.post(`${API_URL}/admin/post-variations${url}`, body, { headers: adminHeaders(), withCredentials: true }),
  put: (url: string, body: any) => axios.put(`${API_URL}/admin/post-variations${url}`, body, { headers: adminHeaders(), withCredentials: true }),
  del: (url: string) => axios.delete(`${API_URL}/admin/post-variations${url}`, { headers: adminHeaders(), withCredentials: true }),
}

export interface PostVariationRow {
  _id: string
  text: string
  contact_mode: 'phone_only' | 'with_link'
  active: boolean
  source: 'ai' | 'manual'
  stale: boolean
  used_count: number
  last_used_at: string | null
}

const MODE_LABEL: Record<string, string> = { phone_only: 'טלפון בלבד', with_link: 'עם קישור' }
const errText = (e: any) => e?.response?.data?.error || e?.message || 'שגיאה'

const PostVariationsModal: React.FC<{ postId: string; onClose: () => void }> = ({ postId, onClose }) => {
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [rows, setRows] = useState<PostVariationRow[]>([])
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [settings, setSettings] = useState<any>(null)
  const [count, setCount] = useState(4)
  const [notice, setNotice] = useState<{ text: string; tone: 'ok' | 'warn' | 'err' } | null>(null)
  const [newText, setNewText] = useState('')
  const [newMode, setNewMode] = useState<'phone_only' | 'with_link'>('with_link')

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/post/${postId}`)
      setRows(res.data.variations || [])
      setDrafts({})
      const s = res.data.account?.settings || {}
      setSettings(s)
      if (s.count) setCount(s.count)
    } catch (e) {
      setNotice({ text: 'טעינת הוריאציות נכשלה: ' + errText(e), tone: 'err' })
    } finally {
      setLoading(false)
    }
  }, [postId])

  useEffect(() => { load() }, [load])

  const generate = async () => {
    if (rows.some(r => r.source === 'ai') && !window.confirm('ליצור מחדש? הוריאציות הקיימות שנוצרו ב-AI יוחלפו (ידניות נשמרות).')) return
    setGenerating(true)
    setNotice(null)
    try {
      const res = await api.post(`/post/${postId}/generate`, { count })
      setRows(res.data.variations || [])
      setDrafts({})
      const rej = res.data.rejected || []
      if (res.data.skipped) setNotice({ text: res.data.skipped, tone: 'warn' })
      else if (!res.data.created) setNotice({ text: 'לא נוצרה אף וריאציה תקינה' + (rej[0] ? ': ' + rej[0].reason : ''), tone: 'err' })
      else setNotice({ text: `נוצרו ${res.data.created} וריאציות` + (rej.length ? ` · ${rej.length} נפסלו (${rej.map((r: any) => r.reason).join('; ')})` : ''), tone: rej.length ? 'warn' : 'ok' })
    } catch (e) {
      setNotice({ text: 'יצירה נכשלה: ' + errText(e), tone: 'err' })
    } finally {
      setGenerating(false)
    }
  }

  const update = async (id: string, patch: any) => {
    setBusy(true)
    try {
      const res = await api.put(`/${id}`, patch)
      setRows(prev => prev.map(r => (r._id === id ? res.data.variation : r)))
      setDrafts(prev => { const n = { ...prev }; delete n[id]; return n })
      setNotice(res.data.warning ? { text: 'נשמר, אבל שימו לב: ' + res.data.warning, tone: 'warn' } : null)
    } catch (e) {
      setNotice({ text: 'שמירה נכשלה: ' + errText(e), tone: 'err' })
    } finally {
      setBusy(false)
    }
  }

  const remove = async (id: string) => {
    if (!window.confirm('למחוק את הוריאציה?')) return
    setBusy(true)
    try {
      await api.del(`/${id}`)
      setRows(prev => prev.filter(r => r._id !== id))
    } catch (e) {
      setNotice({ text: 'מחיקה נכשלה: ' + errText(e), tone: 'err' })
    } finally {
      setBusy(false)
    }
  }

  const add = async () => {
    if (!newText.trim()) return
    setBusy(true)
    try {
      const res = await api.post(`/post/${postId}`, { text: newText, contact_mode: newMode })
      setRows(prev => [...prev, res.data.variation])
      setNewText('')
      setNotice(res.data.warning ? { text: 'נוסף, אבל שימו לב: ' + res.data.warning, tone: 'warn' } : null)
    } catch (e) {
      setNotice({ text: 'הוספה נכשלה: ' + errText(e), tone: 'err' })
    } finally {
      setBusy(false)
    }
  }

  const toneColor = { ok: '#15803d', warn: '#b45309', err: '#b91c1c' }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ direction: 'rtl', background: '#fff', borderRadius: '14px', width: 'min(760px, 96vw)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #eee', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 800, fontSize: '16px', color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className="bi bi-shuffle" style={{ color: '#7c3aed' }} /> וריאציות לפוסט
            <span style={{ fontSize: '11px', fontWeight: 700, color: '#7c3aed', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '6px', padding: '2px 8px' }}>אדמין</span>
            {settings && (
              <span style={{ fontSize: '11px', fontWeight: 600, color: settings.enabled ? '#15803d' : '#9ca3af' }}>
                {settings.enabled ? '● פעיל ללקוח — הבוט מפרסם וריאציה אקראית' : '○ כבוי ללקוח — הבוט מפרסם את המקור'}
              </span>
            )}
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: '22px', cursor: 'pointer', color: '#888', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: '12px 20px', borderBottom: '1px solid #f1f1f4', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: '#475569' }}>כמה ליצור:</span>
          {[3, 4, 5].map(n => (
            <button key={n} onClick={() => setCount(n)} disabled={generating}
              style={{ padding: '4px 12px', borderRadius: 8, border: `1px solid ${count === n ? '#7c3aed' : '#e2e8f0'}`, background: count === n ? '#f5f3ff' : '#fff', color: count === n ? '#7c3aed' : '#475569', fontWeight: 700, cursor: 'pointer' }}>{n}</button>
          ))}
          <button onClick={generate} disabled={generating}
            style={{ marginRight: 'auto', padding: '7px 14px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #7c3aed 0%, #2b70fa 100%)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: generating ? 'wait' : 'pointer', opacity: generating ? 0.7 : 1 }}>
            <i className="bi bi-stars" style={{ marginLeft: 6 }} />{generating ? 'יוצר… (כ-20 שניות)' : rows.some(r => r.source === 'ai') ? 'צור מחדש עם AI' : 'צור וריאציות עם AI'}
          </button>
        </div>

        {notice && <div style={{ padding: '8px 20px', fontSize: 12.5, color: toneColor[notice.tone], background: '#fafafa' }}>{notice.text}</div>}

        <div style={{ padding: '14px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 30, color: '#888' }}>טוען…</div>
          ) : rows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 30, color: '#888' }}>אין עדיין וריאציות לפוסט הזה.</div>
          ) : rows.map((r, i) => {
            const draft = drafts[r._id]
            const dirty = draft !== undefined && draft !== r.text
            return (
              <div key={r._id} style={{ border: `1px solid ${r.stale ? '#fcd34d' : '#e5e7eb'}`, borderRadius: 10, padding: 12, background: r.active ? '#fff' : '#f8fafc', opacity: r.active ? 1 : 0.7 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap', fontSize: 12 }}>
                  <b style={{ color: '#1a1a2e' }}>#{i + 1}</b>
                  <select value={r.contact_mode} disabled={busy} onChange={(e) => update(r._id, { contact_mode: e.target.value })}
                    style={{ fontSize: 12, padding: '2px 6px', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                    <option value="with_link">{MODE_LABEL.with_link}</option>
                    <option value="phone_only">{MODE_LABEL.phone_only}</option>
                  </select>
                  <span style={{ color: '#9ca3af' }}>{r.source === 'ai' ? 'AI' : 'ידני'}</span>
                  {r.stale && <span style={{ color: '#b45309', fontWeight: 700 }} title="הפוסט נערך אחרי שהוריאציה נוצרה — הבוט לא משתמש בה עד שתישמר מחדש או תיווצר מחדש">⚠ לא מעודכנת לפוסט</span>}
                  <span style={{ color: '#9ca3af' }}>פורסמה {r.used_count || 0} פעמים</span>
                  <label style={{ marginRight: 'auto', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    <input type="checkbox" checked={r.active} disabled={busy} onChange={(e) => update(r._id, { active: e.target.checked })} /> פעילה
                  </label>
                  <button onClick={() => remove(r._id)} disabled={busy} title="מחק"
                    style={{ border: 'none', background: 'transparent', color: '#b91c1c', cursor: 'pointer', fontSize: 15 }}><i className="bi bi-trash3" /></button>
                </div>
                <textarea
                  value={draft !== undefined ? draft : r.text}
                  onChange={(e) => setDrafts(prev => ({ ...prev, [r._id]: e.target.value }))}
                  rows={Math.min(14, Math.max(4, (draft ?? r.text).split('\n').length + 1))}
                  style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, fontSize: 13, lineHeight: 1.5, fontFamily: 'inherit', direction: 'rtl', resize: 'vertical' }}
                />
                {(dirty || r.stale) && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    <button onClick={() => update(r._id, { text: draft !== undefined ? draft : r.text })} disabled={busy}
                      style={{ padding: '5px 12px', borderRadius: 7, border: 'none', background: '#2b70fa', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                      {r.stale && !dirty ? 'אשר כמעודכנת לפוסט' : 'שמור'}
                    </button>
                    {dirty && <button onClick={() => setDrafts(prev => { const n = { ...prev }; delete n[r._id]; return n })}
                      style={{ padding: '5px 12px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', fontSize: 12, cursor: 'pointer' }}>בטל</button>}
                  </div>
                )}
              </div>
            )
          })}

          {!loading && (
            <div style={{ border: '1px dashed #cbd5e1', borderRadius: 10, padding: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12.5 }}>
                <b>הוספת וריאציה ידנית</b>
                <select value={newMode} onChange={(e) => setNewMode(e.target.value as any)} style={{ fontSize: 12, padding: '2px 6px', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                  <option value="with_link">{MODE_LABEL.with_link}</option>
                  <option value="phone_only">{MODE_LABEL.phone_only}</option>
                </select>
              </div>
              <textarea value={newText} onChange={(e) => setNewText(e.target.value)} rows={4} placeholder="נוסח הפוסט…"
                style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, fontSize: 13, fontFamily: 'inherit', direction: 'rtl' }} />
              <button onClick={add} disabled={busy || !newText.trim()}
                style={{ marginTop: 6, padding: '5px 12px', borderRadius: 7, border: 'none', background: '#475569', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', opacity: newText.trim() ? 1 : 0.5 }}>הוסף</button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PostVariationsModal
